import { Container, Graphics } from '../../../vendor/pixi.min.mjs';
import { PLATFORMER_ENTITY_DEFS } from './platformerConfig.js';

/**
 * NoteEntity — Mario-Wonder style musical note that lives in the world
 * as a pass-through pickup-shaped element. The collision sweep treats
 * its `onCollidePlayer` verdict as `'note'`, which the runtime's
 * PlatformerNotes controller intercepts to trigger sample-accurate
 * audio playback (a one-shot clip, or one consecutive 1-second slice
 * of an MP3 shared across several notes for a synced sequence).
 *
 * The entity itself is intentionally light:
 *
 *   - holds the live `props` bag (audio slot key, mode, slice index /
 *     duration, retrigger policy, volume) so the notes controller
 *     reads everything from one place;
 *   - draws either the imported note glyph or a stylised eighth-note
 *     fallback so a stage reads correctly even before any art is set;
 *   - bobs in place + can flash on `pulse()` for visual confirmation
 *     when the controller fires its sound.
 *
 * No physics — the body is non-solid (collision mask aside) and never
 * blocks movement; it's purely a sensor like CoinEntity.
 */
export class NoteEntity {
    constructor(c, r, board, theme, props = {}) {
        this._theme = theme;
        this._board = board;
        this.cell = { c, r };
        const cs = board.cellSize;
        const w = cs * 0.65;
        const h = cs * 0.85;

        this.x = c * cs + (cs - w) / 2;
        this.y = r * cs + (cs - h) / 2;
        this.w = w;
        this.h = h;

        this.alive = true;
        this.isNote = true;
        this.props = { ...props };
        // Filled in by PlatformerNotes once it groups + orders all the
        // notes for the current stage. `auto` = let the controller
        // assign one; a fixed number stays untouched.
        this.resolvedSliceIndex = 0;

        this.display = new Container();
        this._buildVisual();
        this._bobBase = this.y;
        this._bobTime = Math.random() * Math.PI * 2;
        this._pulseT = 0;
        this.display.x = this.x;
        this.display.y = this.y;
    }

    _buildVisual() {
        this.display.removeChildren();
        const def = PLATFORMER_ENTITY_DEFS.note;
        const sprite = this._theme?.createSprite?.(def.slot, this.w, this.h, { fitMode: 'contain' });
        if (sprite) {
            sprite.anchor?.set?.(0.5, 0.5);
            sprite.x = this.w / 2;
            sprite.y = this.h / 2;
            this.display.addChild(sprite);
            this._sprite = sprite;
            return;
        }
        // Procedural eighth-note glyph — a filled disc head with a flag
        // stem. Reads instantly as a music note even at a glance.
        const g = new Graphics();
        const headR = Math.min(this.w, this.h) * 0.26;
        const headCX = this.w * 0.32;
        const headCY = this.h * 0.72;
        const stemW = Math.max(3, this.w * 0.07);
        const stemTop = this.h * 0.10;

        g.rect(headCX + headR * 0.6, stemTop, stemW, headCY - stemTop);
        g.fill(def.fallbackColor);

        // Flag sweep at the top of the stem (kept simple so it scales).
        g.moveTo(headCX + headR * 0.6 + stemW, stemTop);
        g.lineTo(headCX + headR * 0.6 + stemW + this.w * 0.20, stemTop + this.h * 0.08);
        g.lineTo(headCX + headR * 0.6 + stemW + this.w * 0.10, stemTop + this.h * 0.20);
        g.lineTo(headCX + headR * 0.6 + stemW, stemTop + this.h * 0.18);
        g.closePath();
        g.fill(def.fallbackColor);

        // Note head — tilted ellipse for that classic shape.
        g.ellipse(headCX, headCY, headR * 1.05, headR * 0.78);
        g.fill(def.fallbackColor);
        g.ellipse(headCX, headCY, headR * 1.05, headR * 0.78);
        g.stroke({ color: 0x1f2937, width: 1.5, alpha: 0.6 });

        this.display.addChild(g);
        this._sprite = g;
    }

    /** Idle bob + decay any active pulse flash. */
    update() {
        if (!this.alive) return;
        this._bobTime += 0.10;
        const offset = Math.sin(this._bobTime) * 2;
        this.display.y = this._bobBase + offset;

        if (this._pulseT > 0) {
            this._pulseT -= 1 / 30;
            // Quick scale + tint flash: scale eases back to 1 from 1.35.
            const k = Math.max(0, this._pulseT * 2);
            const s = 1 + k * 0.35;
            try { this.display.scale.set(s); } catch (_) {}
            this.display.alpha = 1 - k * 0.25;
            if (this._pulseT <= 0) {
                try { this.display.scale.set(1); } catch (_) {}
                this.display.alpha = 1;
            }
        }
    }

    /**
     * Visual confirmation that the controller fired this note's sound.
     * Triggers a short pop + flash that decays over ~0.4s. Idempotent
     * — calling again mid-pulse just refreshes the timer.
     */
    pulse() {
        this._pulseT = 0.4;
    }

    /**
     * Collision verdict the scene's sweep gets when the player overlaps
     * this note. Returning `'note'` is what tells PlatformerScene to
     * forward the contact to the PlatformerNotes controller (which
     * decides whether to actually play, based on the entity's retrigger
     * mode and whether the player just entered or was already inside).
     */
    onCollidePlayer() {
        return this.alive ? 'note' : null;
    }

    destroy() {
        this.display.removeChildren();
        if (this.display.parent) this.display.parent.removeChild(this.display);
    }
}
